import React, { useMemo, useState, useEffect, useRef } from 'react';
import '../styles/CompCardMap.css';
import Marker from './Marker';
import House from '../assets/icons/House';
import MapBubble from '../assets/icons/MapBubble';
import { colour } from '../styles/GlobalStyles';
import { rasterOnSafari } from '../utils/mapRendering';

// ---------------------------------------------------------------------------
// ONE shared map instance for every comp card's distance-hover preview.
//
// This used to render `<GoogleMap>` per card, which built a BRAND NEW
// google.maps.Map on every hover. These are vector maps (`mapId` below), so
// each one takes its own WebGL context — and the Maps JS API has no
// destroy/dispose, so unmounting on mouse-out never gives the context back.
// Browsers cap live WebGL contexts and evict the OLDEST when you go over, so
// after ~a dozen hovers the browser killed the oldest context — the main
// AllCompsMap, still on screen — and its next frame threw Google's own
// `LostContextError` ("getParameter", webgl.js), surfacing as a full-screen
// CRA error overlay on the ARV tab.
//
// Only one card can be hovered at a time, so one map is all we ever need:
// keep a single detached container + Map alive at module scope and move that
// container into whichever card is currently hovered. Contexts created: 1,
// for the life of the tab, no matter how many cards get hovered.
// ---------------------------------------------------------------------------
let sharedMapDiv = null;
let sharedMap = null;

const getSharedMap = () => {
    if (!window.google || !window.google.maps) return null;
    if (sharedMap) return sharedMap;

    sharedMapDiv = document.createElement('div');
    sharedMapDiv.className = 'comp-card-map-shared-surface';

    sharedMap = new window.google.maps.Map(sharedMapDiv, {
        disableDefaultUI: false,
        scrollwheel: true,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        mapId: JSON.parse(process.env.REACT_APP_COMPS_DETAIL_MAP_ID).key,
        // Safari cannot hold the WebGL context a vector map needs — see
        // src/utils/mapRendering.js. No-op everywhere else.
        ...rasterOnSafari()
    });

    return sharedMap;
};

function CompCardMap(props) {
    const isMapLoaded = props.isMapLoaded;
    const isMapActive = props.isMapActive;
    const comp = props.comp;
    const subjectProperty = props.subjectProperty;
    const distance = comp.compDetails.distanceMiles;
    const bestComp = props.bestComp;
    const zoom = distance < 0.5 ? 15 : distance < 0.75 ? 14 : 13;
    const [rawMap, setRawMap] = useState(null);
    const hostRef = useRef(null);
    const directionsServiceRef = useRef(null);
    const directionsRendererRef = useRef(null);

    const subjectLat = subjectProperty.addressDetails.latitude;
    const subjectLng = subjectProperty.addressDetails.longitude;
    const compLat = comp.addressDetails.latitude;
    const compLng = comp.addressDetails.longitude;

    // Coordinates for the two markers
    const startPosition = useMemo(() => ({
        lat: subjectLat,
        lng: subjectLng
    }), [subjectLat, subjectLng]);

    // Adopt the shared map surface into THIS card while it's hovered, and hand
    // it back (detach, not destroy) on mouse-out so the next card can take it.
    // Centre/zoom are re-applied on every adoption because the previous card
    // left the map wherever its own comp was — and because the user may have
    // panned it.
    useEffect(() => {
        if (isMapLoaded !== true || isMapActive !== true) return undefined;

        const host = hostRef.current;
        if (!host) return undefined;

        const map = getSharedMap();
        if (!map) return undefined;

        host.appendChild(sharedMapDiv);
        map.setCenter({ lat: subjectLat, lng: subjectLng });
        map.setZoom(zoom);
        setRawMap(map);

        return () => {
            // Drop our markers/route first (Marker's own cleanup detaches the
            // AdvancedMarkerElements), then pull the surface out of this card.
            setRawMap(null);
            if (sharedMapDiv && sharedMapDiv.parentNode === host) {
                host.removeChild(sharedMapDiv);
            }
        };
    }, [isMapLoaded, isMapActive, subjectLat, subjectLng, zoom]);

    // Create the DirectionsService and DirectionsRenderer once
    useEffect(() => {
        if (window.google && !directionsServiceRef.current && !directionsRendererRef.current) {
            directionsServiceRef.current = new window.google.maps.DirectionsService();
            directionsRendererRef.current = new window.google.maps.DirectionsRenderer({
                polylineOptions: {
                    strokeColor: colour.gray07,
                    strokeOpacity: 0.8,
                    strokeWeight: 6
                },
                markerOptions: {
                icon: {
                    path: window.google.maps.SymbolPath.CIRCLE,
                    scale: 8,
                    fillColor: colour.gray07,
                    fillOpacity: 1,
                    strokeWeight: 0
                }
                },
                preserveViewport: true
            });
        }
    }, []);

    // Whenever the adopted map or the comp changes, route or clear. The
    // renderer is detached on cleanup so a route from the previously hovered
    // card can't linger on the shared surface.
    useEffect(() => {
        if (
            directionsServiceRef.current &&
            directionsRendererRef.current &&
            rawMap &&
            distance > 0.1
        ) {
            const originalPosition = {
                lat: subjectLat,
                lng: subjectLng
            };
            // We have all conditions to request directions
            const destination = {
                lat: compLat,
                lng: compLng
            };
            directionsServiceRef.current.route(
                {
                    origin: originalPosition,
                    destination,
                    travelMode: "DRIVING"
                },
                (result, status) => {
                    if (status === "OK") {
                        // Attach our single DirectionsRenderer to the map
                        directionsRendererRef.current.setMap(rawMap);
                        // Updating directions automatically replaces any previous route
                        directionsRendererRef.current.setDirections(result);
                    } else {
                        console.error("Directions request failed:", status);
                    }
                }
            );
        }
        else if (directionsRendererRef.current) {
            // If user hides directions or there is no clickedComp, remove from map
            directionsRendererRef.current.setMap(null);
            directionsRendererRef.current.setDirections({ routes: [] });
        }

        return () => {
            if (directionsRendererRef.current) {
                directionsRendererRef.current.setMap(null);
            }
        };
    }, [subjectLat, subjectLng, rawMap, compLat, compLng, distance]);

    const compsMarker = useMemo(() => {
        if (!rawMap || !comp) return null; // Ensure map and comp exist

        return {
                comp,
                position: {
                    lat: compLat,
                    lng: compLng
                }
            };
    }, [comp, rawMap, compLat, compLng]);

    if (isMapLoaded !== true || isMapActive !== true) {
        return <div className="comp-card-map-placeholder" />;
    }

    return (
        <>
            <div ref={hostRef} className="comp-card-map-container" />
            {
                rawMap !== null && compsMarker !== null &&
                <Marker
                    key={comp.uniqueIds.zillowId}
                    map={rawMap}
                    position={compsMarker.position}
                    onClick={() => null}
                >
                    <div className="relative-container">
                        <MapBubble
                            className=""
                            fill={bestComp === true ? colour.jade01 : colour.gray02}
                            stroke={bestComp === true ? colour.jade02 : colour.gray03}
                            strokeWidth={"0.5"}
                        />
                        <House
                            fill={bestComp === true ? colour.jade03 : colour.gray04}
                            className="cluster-map-house-icon"
                        />
                    </div>
                </Marker>
            }
            {
                rawMap !== null &&
                <Marker
                    map={rawMap}
                    position={startPosition}
                    onClick={() => null}
                >
                    <div className="relative-container">
                        <MapBubble
                            className=""
                            fill={colour.gray07}
                            stroke={colour.gray00}
                            strokeWidth={"0.5"}
                        />
                        <House
                            fill={colour.gray00}
                            className="comps-map-house-icon"
                        />
                    </div>
                </Marker>
            }
        </>
    )
};

export default CompCardMap;
